import { cn } from "@/lib/utils";
import { PaginatedResponse } from "@/types/common";
import { ColumnDef, ColumnResizeMode, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { ArrowDown, ArrowUp } from "lucide-react";
import React, { useMemo, useState } from "react";
import { Await, Location, useLocation, useNavigate } from "react-router-dom";
import { Checkbox } from "../ui/checkbox";
import { Skeleton } from "../ui/skeleton";
import { FieldLabelWithDescription } from "./field-label-with-description";
import { ObjectListColumn } from "./types2";

interface ObjectListTableResizableProps<T> {
    paginated_response: PaginatedResponse<T>;
    columns: ObjectListColumn<T>[];
    onRowNavigateTo?: (
        instance: T,
        currentLocation: Location,
        event?: React.MouseEvent<HTMLTableCellElement>
    ) => string | Map<string, string> | null;
    allowAutoRedirect?: boolean;
    allowBatchSelection?: boolean;
    itemToId?: (item: T) => string;
    selectedItems?: T[];
    setSelectedItems?: (items: T[]) => void;
    selectAll?: boolean;
    setSelectAll?: (selectAll: boolean) => void;
    primary_column?: string;
    allowColumnResizing?: boolean;
    allowColumnOrdering?: boolean;
    allowLateralScroll?: boolean;
    layout?: "default" | "dense";
    onAutoResize?: () => void;
    orderBy?: { column: string; direction: "asc" | "desc" } | null;
    onOrderByChange?: (orderBy: { column: string; direction: "asc" | "desc" } | null) => void;
    hasActiveFilters?: boolean;
    isLoading?: boolean;
    clickable_field?: string;
    error?: { status?: number; message?: string } | null;
    expandedRowId?: string | null;
    renderExpandedRow?: (item: T) => React.ReactNode;
}

// // Fonction pour calculer la largeur optimale d'une colonne
// function calculateOptimalColumnWidth<T>(column: ObjectListColumn<T>, data: T[], layout: "default" | "dense"): number {
//     // Si une taille est déjà définie, l'utiliser
//     if (column.size) {
//         return column.size;
//     }

//     // Calculer la largeur basée sur le titre
//     const headerLength = column.name.length;
//     let headerWidth = Math.max(headerLength * 8, 80); // ~8px par caractère, minimum 80px

//     // Si on a des données, calculer la largeur basée sur le contenu
//     if (data.length > 0) {
//         let maxContentLength = 0;

//         // Prendre un échantillon des données (max 20 éléments pour la performance)
//         const sampleData = data.slice(0, 20);

//         for (const item of sampleData) {
//             let contentLength = 0;

//             if (column.cellFn) {
//                 // Pour les cellFn, on ne peut pas facilement mesurer, utiliser une estimation
//                 contentLength = 20;
//             } else if (column.accessorFn) {
//                 const value = column.accessorFn(item);
//                 contentLength = String(value || "").length;
//             } else if (column.accessorKey) {
//                 const value = item[column.accessorKey];
//                 contentLength = String(value || "").length;
//             }

//             maxContentLength = Math.max(maxContentLength, contentLength);
//         }

//         // Convertir la longueur en pixels (environ 8px par caractère)
//         const contentWidth = maxContentLength * 8;

//         // Prendre le maximum entre le header et le contenu, avec padding
//         const optimalWidth = Math.max(headerWidth, contentWidth) + (layout === "dense" ? 8 : 32); // Add less padding for dense layout

//         // Appliquer des limites raisonnables
//         return Math.min(Math.max(optimalWidth, 100), 400);
//     }

//     // Si pas de données, utiliser juste la largeur du header
//     return Math.min(Math.max(headerWidth + (layout === "dense" ? 8 : 32), 100), 400);
// }

function ResizableTableContent<T>({
    data,
    columns,
    onRowNavigateTo,
    allowBatchSelection,
    selectedItems,
    setSelectedItems,
    selectAll,
    setSelectAll,
    primary_column,
    allowColumnResizing = true,
    allowLateralScroll = true,
    layout = "default",
    onAutoResize,
    orderBy,
    onOrderByChange,
    hasActiveFilters = false,
    isLoading = false,
    clickable_field,
    error = null,
    expandedRowId,
    renderExpandedRow,
    itemToId
}: {
    data: T[];
    columns: ObjectListColumn<T>[];
    onRowNavigateTo?: (
        instance: T,
        currentLocation: Location,
        event?: React.MouseEvent<HTMLTableCellElement>
    ) => string | Map<string, string> | null;
    allowBatchSelection?: boolean;
    selectedItems?: T[];
    setSelectedItems?: (items: T[]) => void;
    selectAll?: boolean;
    setSelectAll?: (selectAll: boolean) => void;
    primary_column?: string;
    allowColumnResizing?: boolean;
    allowColumnOrdering?: boolean;
    allowLateralScroll?: boolean;
    layout?: "default" | "dense";
    onAutoResize?: () => void;
    orderBy?: { column: string; direction: "asc" | "desc" } | null;
    onOrderByChange?: (orderBy: { column: string; direction: "asc" | "desc" } | null) => void;
    hasActiveFilters?: boolean;
    isLoading?: boolean;
    clickable_field?: string;
    error?: { status?: number; message?: string } | null;
    expandedRowId?: string | null;
    renderExpandedRow?: (item: T) => React.ReactNode;
    itemToId?: (item: T) => string;
}) {
    const navigate = useNavigate();
    const location = useLocation();
    const [columnResizeMode] = useState<ColumnResizeMode>("onChange");
    const [draggedColumn, setDraggedColumn] = useState<number | null>(null);
    const [dragOverColumn, setDragOverColumn] = useState<number | null>(null);
    const [lastClickedRowIndex, setLastClickedRowIndex] = useState<number | null>(null);

    const getItemId = (item: T): string | undefined => (itemToId ? itemToId(item) : undefined);

    const isItemInList = (items: T[], item: T): boolean => {
        if (!items.length) return false;
        const itemId = getItemId(item);
        if (itemId !== undefined) {
            return items.some(selected => getItemId(selected) === itemId);
        }
        return items.includes(item);
    };

    const isItemSelected = (item: T): boolean => isItemInList(selectedItems ?? [], item);

    const addToSelection = (items: T[], item: T): T[] => {
        if (isItemInList(items, item)) return items;
        const itemId = getItemId(item);
        if (itemId !== undefined) {
            return [...items.filter(selected => getItemId(selected) !== itemId), item];
        }
        return [...items, item];
    };

    const removeFromSelection = (items: T[], item: T): T[] => {
        const itemId = getItemId(item);
        if (itemId !== undefined) {
            return items.filter(selected => getItemId(selected) !== itemId);
        }
        return items.filter(selected => selected !== item);
    };

    const removeManyFromSelection = (items: T[], toRemove: T[]): T[] => {
        const idsToRemove = new Set(toRemove.map(item => getItemId(item) ?? item));
        return items.filter(item => {
            const itemId = getItemId(item);
            if (itemId !== undefined) return !idsToRemove.has(itemId);
            return !toRemove.includes(item);
        });
    };

    const mergeIntoSelection = (items: T[], toAdd: T[]): T[] => {
        const byId = new Map<string, T>();
        for (const item of items) {
            const itemId = getItemId(item);
            byId.set(itemId ?? String(item), item);
        }
        for (const item of toAdd) {
            const itemId = getItemId(item);
            byId.set(itemId ?? String(item), item);
        }
        return Array.from(byId.values());
    };

    const handleOrderingChange = (column: ObjectListColumn<T>) => {
        if (!onOrderByChange || !column.fieldName) return;

        const fieldName = column.fieldName;

        if (!orderBy || orderBy.column !== fieldName) {
            // Set ascending order
            onOrderByChange({ column: fieldName, direction: "asc" });
        } else if (orderBy.direction === "asc") {
            // Switch to descending order
            onOrderByChange({ column: fieldName, direction: "desc" });
        } else {
            // Clear ordering
            onOrderByChange(null);
        }
    };

    const handleColumnDragStart = (e: React.DragEvent, columnIndex: number) => {
        if (!allowColumnResizing) return;
        setDraggedColumn(columnIndex);
        e.dataTransfer.effectAllowed = "move";
    };

    const handleColumnDragEnd = () => {
        setDraggedColumn(null);
        setDragOverColumn(null);
    };

    // Fonction pour calculer la largeur optimale d'une colonne
    function calculateOptimalColumnWidth(column: ObjectListColumn<T>, data: T[], layout: "default" | "dense"): number {
        // Si une taille est déjà définie, l'utiliser
        if (column.size) {
            return column.size;
        }

        // Calculer la largeur basée sur le titre
        const headerLength = column.name.length;
        let headerWidth = Math.max(headerLength * 8, 80); // ~8px par caractère, minimum 80px

        // Si on a des données, calculer la largeur basée sur le contenu
        if (data.length > 0) {
            let maxContentLength = 0;

            // Prendre un échantillon des données (max 20 éléments pour la performance)
            const sampleData = data.slice(0, 20);

            for (const item of sampleData) {
                let contentLength = 0;

                if (column.cellFn) {
                    // Pour les cellFn, on ne peut pas facilement mesurer, utiliser une estimation
                    contentLength = 20;
                } else if (column.accessorFn) {
                    const value = column.accessorFn(item);
                    contentLength = String(value || "").length;
                } else if (column.accessorKey) {
                    const value = item[column.accessorKey];
                    contentLength = String(value || "").length;
                }

                maxContentLength = Math.max(maxContentLength, contentLength);
            }

            // Convertir la longueur en pixels (environ 8px par caractère)
            const contentWidth = maxContentLength * 8;

            // Prendre le maximum entre le header et le contenu, avec padding
            const optimalWidth = Math.max(headerWidth, contentWidth) + (layout === "dense" ? 8 : 32); // Add less padding for dense layout

            // Appliquer des limites raisonnables
            return Math.min(Math.max(optimalWidth, 100), 400);
        }

        // Si pas de données, utiliser juste la largeur du header
        return Math.min(Math.max(headerWidth + (layout === "dense" ? 8 : 32), 100), 400);
    }

    // Convertir les colonnes ObjectListColumn en ColumnDef pour react-table
    const tableColumns = useMemo<ColumnDef<T>[]>(() => {
        const cols: ColumnDef<T>[] = [];

        // Colonne d'index (numéro de ligne)
        if (layout !== "dense") {
            cols.push({
                id: "row-index",
                size: 40,
                minSize: 40,
                maxSize: 40,
                enableResizing: false,
                header: () => <span className="ml-1 flex items-center justify-center text-xs text-gray-500">#</span>,
                cell: ({ row }) => <span className="ml-1 flex items-center justify-center text-xs text-gray-500">{row.index + 1}</span>
            });
        }

        // Colonne de sélection si nécessaire
        if (allowBatchSelection) {
            cols.push({
                id: "select",
                size: 30,
                minSize: 30,
                maxSize: 30,
                enableResizing: false,
                header: () => (
                    <Checkbox
                        checked={selectAll || (Boolean(selectedItems?.length) && selectedItems?.length === data.length)}
                        onCheckedChange={checked => {
                            if (checked === true) {
                                setSelectedItems?.(data);
                            } else {
                                setSelectedItems?.([]);
                                setSelectAll?.(false);
                            }
                        }}
                    />
                ),
                cell: ({ row }) => (
                    <Checkbox
                        checked={isItemSelected(row.original)}
                        onCheckedChange={checked => {
                            // Fallback when triggered by keyboard/assistive tech
                            if (checked === true) {
                                setSelectedItems?.(addToSelection(selectedItems ?? [], row.original));
                            } else {
                                setSelectedItems?.(removeFromSelection(selectedItems ?? [], row.original));
                                setSelectAll?.(false);
                            }
                        }}
                        onClick={e => {
                            e.stopPropagation();
                            if (!setSelectedItems) return;

                            const isCurrentlyChecked = isItemSelected(row.original);

                            if (e.shiftKey && lastClickedRowIndex !== null) {
                                e.preventDefault();
                                const start = Math.min(lastClickedRowIndex, row.index);
                                const end = Math.max(lastClickedRowIndex, row.index);
                                const rangeItems = data.slice(start, end + 1);

                                if (isCurrentlyChecked && rangeItems.every(item => isItemSelected(item))) {
                                    const next = removeManyFromSelection(selectedItems ?? [], rangeItems);
                                    setSelectedItems(next);
                                    setSelectAll?.(false);
                                } else {
                                    // Keep selectAll=false: selecting all *displayed* rows is not
                                    // "Select all N matching". The banner offers that explicitly.
                                    const next = mergeIntoSelection(selectedItems ?? [], rangeItems);
                                    setSelectedItems(next);
                                }
                            } else {
                                // Single toggle
                                if (isCurrentlyChecked) {
                                    const next = removeFromSelection(selectedItems ?? [], row.original);
                                    setSelectedItems(next);
                                    setSelectAll?.(false);
                                } else {
                                    // Keep selectAll=false: selecting all *displayed* rows is not
                                    // "Select all N matching". The banner offers that explicitly.
                                    const next = addToSelection(selectedItems ?? [], row.original);
                                    setSelectedItems(next);
                                }
                            }

                            setLastClickedRowIndex(row.index);
                        }}
                    />
                )
            });
        }

        // Calculer les largeurs optimales pour toutes les colonnes
        const columnSizes = columns.filter(c => c.hidden !== true).map(column => calculateOptimalColumnWidth(column, data, layout));
        const hasClickableField = columns
            .filter(c => c.hidden !== true)
            .some(column => (clickable_field && column.fieldName === clickable_field) || (primary_column && column.fieldName === primary_column));

        // Ajouter les colonnes définies par l'utilisateur
        columns
            .filter(c => c.hidden !== true)
            .forEach((column, index) => {
                // Calculer la largeur optimale
                const optimalSize = columnSizes[index];

                cols.push({
                    id: column.key || `${column.name}_${index}`,
                    accessorKey: column.accessorKey as string,
                    accessorFn: column.accessorFn ? (row: T) => column.accessorFn!(row) : undefined,
                    size: optimalSize,
                    minSize: column.minSize || 50,
                    maxSize: column.maxSize || 500,
                    enableResizing: column.enableResizing !== false,
                    header: () => {
                        const canSort = Boolean(column.fieldName);
                        const isCurrentSort = orderBy?.column === column.fieldName;

                        const headerContent = canSort ? (
                            (() => {
                                let IconComponent = null;
                                if (isCurrentSort) {
                                    IconComponent = orderBy?.direction === "asc" ? ArrowUp : ArrowDown;
                                }

                                return (
                                    <div
                                        className="flex cursor-pointer items-center justify-between"
                                        style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                        onClick={() => handleOrderingChange(column)}
                                    >
                                        <FieldLabelWithDescription
                                            label={column.name}
                                            description={column.description}
                                            className="min-w-0 truncate"
                                        />
                                        {IconComponent && <IconComponent className="ml-1 h-4 w-4 flex-shrink-0" strokeWidth={2} />}
                                    </div>
                                );
                            })()
                        ) : (
                            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                <FieldLabelWithDescription
                                    label={column.name}
                                    description={column.description}
                                    className="min-w-0 truncate"
                                />
                            </div>
                        );

                        if (allowColumnResizing) {
                            return (
                                <div draggable onDragStart={e => handleColumnDragStart(e, index)} onDragEnd={handleColumnDragEnd}>
                                    {headerContent}
                                </div>
                            );
                        }

                        return headerContent;
                    },
                    cell: ({ row }) => {
                        const columnNavigateTo = column.navigateTo ? column.navigateTo(row.original) : null;
                        const hasColumnNavigation = Boolean(columnNavigateTo);
                        const isClickable =
                            (clickable_field && column.fieldName === clickable_field) ||
                            (primary_column && column.fieldName === primary_column) ||
                            (!hasClickableField && index === 0) ||
                            hasColumnNavigation;

                        const baseClassName = isClickable ? "text-blue-700 hover:text-blue-900 cursor-pointer" : "";

                        const onClickAction = (event: React.MouseEvent<HTMLTableCellElement>) => {
                            // If column has its own navigation, use it
                            if (hasColumnNavigation && columnNavigateTo) {
                                if (event.metaKey || event.ctrlKey) {
                                    const win = window.open(columnNavigateTo, "_blank");
                                    win?.focus();
                                } else {
                                    navigate(columnNavigateTo);
                                }
                                return;
                            }

                            // Otherwise, use row navigation
                            if (onRowNavigateTo) {
                                const navigateTo = onRowNavigateTo(row.original, location, event);
                                if (navigateTo === null) {
                                    return;
                                }

                                if (navigateTo instanceof Map) {
                                    if (navigateTo.size === 0) {
                                        return;
                                    }
                                    const newSearchParams = new URLSearchParams();
                                    navigateTo.forEach((v, k) => {
                                        newSearchParams.set(k, v);
                                    });
                                    if (event.metaKey || event.ctrlKey) {
                                        const win = window.open(location.pathname + "?" + newSearchParams.toString(), "_blank");
                                        win?.focus();
                                    } else {
                                        navigate(location.pathname + "?" + newSearchParams.toString());
                                    }
                                } else {
                                    if (event.metaKey || event.ctrlKey) {
                                        const win = window.open(navigateTo, "_blank");
                                        win?.focus();
                                    } else {
                                        navigate(navigateTo);
                                    }
                                }
                            }
                        };

                        if (column.cellFn) {
                            return (
                                <div
                                    className={`truncate ${baseClassName}`}
                                    style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                    onClick={isClickable ? onClickAction : undefined}
                                >
                                    {column.cellFn(row.original)}
                                </div>
                            );
                        } else if (column.accessorFn) {
                            return (
                                <div
                                    className={cn("truncate", baseClassName, column.cellClassNameFn ? column.cellClassNameFn(row.original) : (column.cellClassName ?? ""))}
                                    style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                    onClick={isClickable ? onClickAction : undefined}
                                >
                                    {column.accessorFn(row.original)}
                                </div>
                            );
                        } else if (column.accessorKey) {
                            const cellValue = row.original[column.accessorKey];
                            
                            // Handle structured field types (company, etc.)
                            let displayValue: string | number | null | undefined;
                            if (cellValue && typeof cellValue === 'object' && 'type' in cellValue && 'value' in cellValue) {
                                const structuredValue = cellValue as { type: string; value: { name?: string } };
                                if (structuredValue.type === 'company' && structuredValue.value) {
                                    displayValue = structuredValue.value.name || '';
                                } else {
                                    displayValue = String(cellValue);
                                }
                            } else {
                                displayValue = cellValue as string | number | null | undefined;
                            }
                            
                            return (
                                <div
                                    className={cn("truncate", baseClassName, column.cellClassNameFn ? column.cellClassNameFn(row.original) : (column.cellClassName ?? ""))}
                                    style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                    onClick={isClickable ? onClickAction : undefined}
                                >
                                    {displayValue ? `${displayValue}` : null}
                                </div>
                            );
                        }
                        return null;
                    }
                });
            });

        return cols;
    }, [
        columns,
        allowBatchSelection,
        selectedItems,
        selectAll,
        data,
        primary_column,
        allowColumnResizing,
        allowLateralScroll,
        draggedColumn,
        dragOverColumn,
        orderBy,
        onOrderByChange,
        clickable_field
    ]);

    const table = useReactTable({
        data,
        columns: tableColumns,
        getCoreRowModel: getCoreRowModel(),
        columnResizeMode,
        enableColumnResizing: true
    });

    // Fonction pour redimensionner automatiquement toutes les colonnes
    const autoResizeColumns = () => {
        // Recalculer les largeurs optimales pour toutes les colonnes
        const newSizes: { [key: string]: number } = {};

        // Pour la colonne de sélection
        if (allowBatchSelection) {
            newSizes["select"] = 40;
        }

        // Pour les colonnes de données
        columns
            .filter(c => c.hidden !== true)
            .forEach((column, index) => {
                const optimalSize = calculateOptimalColumnWidth(column, data, layout);
                const columnId = column.key || `${column.name}_${index}`;
                newSizes[columnId] = optimalSize;
            });

        // Appliquer les nouvelles tailles à react-table
        Object.entries(newSizes).forEach(([columnId, size]) => {
            const column = table.getColumn(columnId);
            if (column) {
                column.resetSize();
                // Forcer la nouvelle taille via l'API interne
                (column as any).columnDef.size = size;
            }
        });

        // Forcer un re-render du tableau
        table.getAllColumns().forEach(col => col.resetSize());
    };

    // Exposer la fonction via onAutoResize
    React.useEffect(() => {
        if (onAutoResize) {
            (window as any).autoResizeTableColumns = autoResizeColumns;
        }
    }, [data, columns, onAutoResize]);

    // Calculer les pourcentages relatifs si pas de défilement latéral
    const getColumnStyle = (column: any) => {
        if (allowLateralScroll) {
            return { width: column.getSize() };
        } else {
            // Calculer le total des largeurs pour convertir en pourcentages
            const totalWidth = table.getHeaderGroups()[0]?.headers.reduce((sum, header) => sum + header.getSize(), 0) || 1;
            const percentage = (column.getSize() / totalWidth) * 100;
            return { width: `${percentage}%` };
        }
    };

    return (
        <div className={allowLateralScroll ? "" : "overflow-hidden"}>
            <table
                className={cn("w-full caption-bottom text-sm", "")}
                style={{
                    tableLayout: "fixed",
                    width: "100%"
                }}
            >
                <thead className={cn("bg-gray-100", layout === "dense" && !allowLateralScroll ? "" : "[&_tr]:border-b")}>
                    {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header, headerIndex) => (
                                <th
                                    key={header.id}
                                    className={cn(
                                        "sticky -top-px z-20 divide-x border border-l-0 border-r-0 bg-gray-100 px-2 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pb-1 [&:has([role=checkbox])]:pr-0",
                                        headerIndex === 0 && !allowBatchSelection && "pl-6",
                                        layout === "dense" && !allowLateralScroll ? "border-none py-1.5 font-normal text-black first:rounded-l last:rounded-r" : "py-1.5 "
                                    )}
                                    style={getColumnStyle(header.column)}
                                >
                                    <div className="truncate pb-0" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                                    </div>
                                    {header.column.getCanResize() && allowColumnResizing && (
                                        <div
                                            onMouseDown={header.getResizeHandler()}
                                            onTouchStart={header.getResizeHandler()}
                                            className={cn(
                                                "absolute right-0 top-0 h-full w-1 cursor-col-resize touch-none select-none bg-transparent hover:bg-blue-500",
                                                header.column.getIsResizing() ? "bg-blue-500" : ""
                                            )}
                                            style={{ transform: "translateX(50%)" }}
                                        />
                                    )}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody className="overflow-hidden">
                    {isLoading && table.getRowModel().rows.length === 0 ? (
                        Array.from({ length: 8 }).map((_, rowIndex) => (
                            <tr key={`skeleton_${rowIndex}`} className="border-b transition-colors last:border-b-0">
                                {table.getAllColumns().map(column => (
                                    <td key={`skeleton_${rowIndex}_${column.id}`} className={cn("px-2 align-middle", layout === "dense" ? "border-none py-1" : "py-1.5")} style={getColumnStyle(column)}>
                                        <Skeleton className="h-4 w-full" />
                                    </td>
                                ))}
                            </tr>
                        ))
                    ) : !isLoading && table.getRowModel().rows.length === 0 ? (
                        <tr className="border-b transition-colors">
                            <td colSpan={table.getAllColumns().length} className="p-4 py-6 text-center align-middle text-gray-500">
                                {error?.status === 403 ? "You don't have access to this object" : hasActiveFilters ? "No result found with current filters" : "No result found"}
                            </td>
                        </tr>
                    ) : (
                        table.getRowModel().rows.map(row => {
                            const rowId = itemToId ? itemToId(row.original) : row.id;
                            const isExpanded = expandedRowId === rowId;
                            
                            return (
                                <React.Fragment key={`row_${row.id}`}>
                                    <tr className="border-b transition-colors last:border-b-0 hover:bg-gray-100/50 data-[state=selected]:bg-gray-100">
                                        {row.getVisibleCells().map((cell, cellIndex) => (
                                            <td
                                                key={cell.id}
                                                className={cn(
                                                    "overflow-hidden px-2 align-middle [&:has([role=checkbox])]:pb-1 [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
                                                    cellIndex === 0 && !allowBatchSelection && "pl-6",
                                                    layout === "dense" ? "border-none py-1 first:rounded-l last:rounded-r" : "py-1.5"
                                                )}
                                                style={getColumnStyle(cell.column)}
                                            >
                                                <div className="truncate" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </div>
                                            </td>
                                        ))}
                                    </tr>
                                    {isExpanded && renderExpandedRow && (
                                        <tr key={`expanded_${row.id}`} className="border-b">
                                            <td colSpan={table.getAllColumns().length} className="p-0">
                                                {renderExpandedRow(row.original)}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })
                    )}
                </tbody>
            </table>
        </div>
    );
}

export function ObjectListTableResizable<T>(props: ObjectListTableResizableProps<T>) {
    return (
        <React.Suspense
            fallback={
                <div className="w-full">
                    <div className="overflow-auto">
                        <table className="w-full caption-bottom text-sm">
                            <thead className=" [&_tr]:border-b">
                                <tr>
                                    {props.allowBatchSelection && (
                                        <th className=" h-10 w-[40px] items-center px-2 text-center font-medium text-gray-500">
                                            <Checkbox disabled />
                                        </th>
                                    )}
                                    {props.columns
                                        .filter(c => c.hidden !== true)
                                        .map((column, index) => (
                                            <th key={`${column.name}_${index}`} className="h-10 px-2 text-left align-middle font-medium text-gray-500">
                                                <FieldLabelWithDescription
                                                    label={column.name}
                                                    description={column.description}
                                                    className="min-w-0 truncate"
                                                />
                                            </th>
                                        ))}
                                </tr>
                            </thead>
                            <tbody className="[&_tr:last-child]:border-0">
                                <tr className="border-b transition-colors hover:bg-gray-100/50 data-[state=selected]:bg-gray-100">
                                    <td colSpan={props.columns.length + (props.allowBatchSelection ? 1 : 0)} className="p-2 py-10 text-center align-middle">
                                        Loading...
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            }
        >
            <Await resolve={props.paginated_response} errorElement={<p>An error occurred</p>}>
                {(paginated_response: PaginatedResponse<T>) => (
                    <ResizableTableContent
                        data={paginated_response.results}
                        columns={props.columns}
                        onRowNavigateTo={props.onRowNavigateTo}
                        allowBatchSelection={props.allowBatchSelection}
                        selectedItems={props.selectedItems}
                        setSelectedItems={props.setSelectedItems}
                        selectAll={props.selectAll}
                        setSelectAll={props.setSelectAll}
                        primary_column={props.primary_column}
                        allowColumnResizing={props.allowColumnResizing}
                        allowColumnOrdering={props.allowColumnOrdering}
                        allowLateralScroll={props.allowLateralScroll}
                        layout={props.layout}
                        onAutoResize={props.onAutoResize}
                        orderBy={props.orderBy}
                        onOrderByChange={props.onOrderByChange}
                        hasActiveFilters={props.hasActiveFilters}
                        isLoading={props.isLoading}
                        clickable_field={props.clickable_field}
                        error={props.error}
                        expandedRowId={props.expandedRowId}
                        renderExpandedRow={props.renderExpandedRow}
                        itemToId={props.itemToId}
                    />
                )}
            </Await>
        </React.Suspense>
    );
}
